<template>
  <section
    v-if="!isLite && mostPlayed && mostPlayed.length > 0"
    class="section"
  >
    <slot name="header">
      <SectionHeader
        :title="$t('sections.mostPlayed')"
        variant="simple"
      />
    </slot>
    
    <div class="cards-grid">
      <LotteryCard
        v-for="game in displayedMostPlayed"
        :key="game.id"
        :lottery="game"
        layout="horizontal"
        :is-favorited="isFavorited"
        @favorite="handleFavorite(game)"
        @bet="handleBet(game)"
        @remove="() => $emit('remove', game)"
      />
    </div>

    <div
      v-if="hasMore"
      class="load-more-container"
    >
      <button
        class="load-more-button"
        @click="loadMore"
      >
        {{ $t('forms.loadMoreNumberGames') }}
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, defineProps, defineEmits } from "vue";
import LotteryCard from "@/components/LotteryCard.vue";
import SectionHeader from "@/components/base/SectionHeader.vue";
import type { UpcomingDraw } from '@/types/lottery';
import { iframeCommunication } from "@/utils/iframeCommunication";
import { useIntegrationMode } from '@/composables/useIntegrationMode';

const { isLite } = useIntegrationMode();

const props = defineProps<{
  mostPlayed: UpcomingDraw[]
  isFavorited?: (draw: UpcomingDraw) => boolean
}>();

const emit = defineEmits<{
  (e: 'bet', game: UpcomingDraw): void;
  (e: 'favorite', game: UpcomingDraw): void;
  (e: 'remove', game: UpcomingDraw): void;
}>();

const displayLimit = ref(12);

const displayedMostPlayed = computed(() => {
  return props.mostPlayed.slice(0, displayLimit.value);
});

const hasMore = computed(() => {
  return props.mostPlayed.length > displayLimit.value;
});

const loadMore = () => {
  displayLimit.value += 10;
  nextTick(() => {
    setTimeout(() => {
      iframeCommunication.sendImmediateHeightUpdate();
    }, 100);
  });
};

const handleBet = (lottery: UpcomingDraw) => {
  emit('bet', lottery);
};

const handleFavorite = (game: UpcomingDraw) => {
  emit('favorite', game);
};
</script>

<script lang="ts">
export default {
  name: 'MostPlayedSection'
}
</script>

<style scoped>
.section {
  margin-bottom: 48px;
}

.section-title.medium {
  color: var(--colour-icons);
  font-family: 'Ubuntu', sans-serif;
  font-size: 20px;
  font-weight: 500;
  text-align: left;
  line-height: 1;
  margin: 0;
  display: flex;
  align-items: center;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--most-played-grid-gap, var(--horizontal-lottery-grid-gap, 12px));
  padding: var(--most-played-grid-padding-y, 8px) 0;
  width: 100%;
  max-width: 1266px; /* (627 * 2) + 12 */
  overflow: hidden;
  align-items: start;
}

.cards-grid > * {
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

:deep(.section-header) {
  margin-bottom: var(--section-header-simple-margin-bottom, 16px);
}

/* Tablet/iPad breakpoint - stay in 2 columns until mobile (768px) */
@media (max-width: 768px) {
  .section {
    padding: 0;
    margin: 0 0 32px 0;
  }

  :deep(.section-header) {
    margin-bottom: var(--section-header-simple-mobile-margin-bottom, 16px);
  }

  .cards-grid {
    grid-template-columns: 1fr;
    gap: var(--most-played-grid-mobile-gap, var(--most-played-grid-gap, var(--horizontal-lottery-grid-mobile-gap, 8px)));
    padding: 0;
  }
}

/* Desktop/Tablet optimization - ensure 2 columns stay from 769px and above */
@media (min-width: 769px) {
  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--most-played-grid-gap, var(--horizontal-lottery-grid-gap, 12px));
  }
}
</style>
